import { EventName } from "lib/types/analytics";
import { BillingType, ItemType } from "lib/types/checkout";
import { UserType } from "lib/types/user";
import { analyticsEvents, ClevertapEvents } from "lib/utils/constants";
import { loadScript, processCheckoutDetails, sleep } from "lib/utils/helpers";

let isClevertapLoading = false;

type CleverTapData = {
  accountId: string;
  region: string;
}

export const getClevertapRegion = (region: string) => {
  switch (region) {
    case 'IN':
      return 'in1';
    case 'EU':
      return 'eu1';
    case 'US':
      return 'us1';
    default:
      return 'in1';
  }
}

const initializeClevertap = (clevertapData: CleverTapData) => {
  isClevertapLoading = true;
  (window as any).clevertap = { event: [], profile: [], account: [], onUserLogin: [], region: getClevertapRegion(clevertapData.region), notifications: [], privacy: [] };
  (window as any).clevertap.account.push({ "id": clevertapData.accountId });
  (window as any).clevertap.privacy.push({ optOut: false }); //set the flag to true, if the user of the device opts out of sharing their data
  (window as any).clevertap.privacy.push({ useIP: false }); //set the flag to true, if the user agrees to share their IP data

  loadScript(('https:' == document.location.protocol ? 'https://d2r1yp2w7bby2u.cloudfront.net' : 'http://static.clevertap.com') + '/js/clevertap.min.js', () => {
    isClevertapLoading = false;
  });
};

export const getClevertapEventPayload = (
  checkoutItems: ItemType[],
  billing: BillingType,
  channel: string,
  metaData: any,
  eventName?: EventName
) => {
  const totalAmount = billing?.total_payable ?? 0;
  const chargedAmount = metaData?.orderAmount ?? totalAmount;
  const isChargedEvent = eventName === analyticsEvents.FLO_PAYMENT_COMPLETED;

  const lineItems = processCheckoutDetails(checkoutItems, "product_id");
  const productNames = processCheckoutDetails(checkoutItems, "item_title");
  const variantIds = processCheckoutDetails(checkoutItems, "variant_id");
  const quantities = processCheckoutDetails(checkoutItems, "quantity");
  const totalQuantity = checkoutItems.reduce((acc, item) => acc + item.quantity, 0);
  const discount = billing?.discount ?? 0;

  const payload = {
    "Source": "Shopflo",
    "Checkout URL": window.location.href,
    "Channel": channel,
    "Product Line Items": lineItems,
    "Product Names": productNames,
    "Product Variant Ids": variantIds,
    "Product Quantities": quantities,
    "Total Products": totalQuantity,
    "Total Amount": totalAmount,
    "Discount": discount,
    ...(metaData?.couponData?.coupon_code && { "Coupon Code": metaData?.couponData?.coupon_code }),
    ...(metaData?.couponData?.coupon_id && { "Coupon ID": metaData?.couponData?.coupon_id }),
    ...(metaData?.couponData?.failed_reason && { "Failed Reason": metaData?.couponData?.failed_reason }),
    ...(metaData?.paymentData?.paymentMethod && { "Payment Method": metaData?.paymentData?.paymentMethod }),
  };

  if (!isChargedEvent) return payload;

  return {
    ...payload,
    "Amount": chargedAmount,
    "Charged ID": metaData?.checkoutId,
    "Items": checkoutItems.map((item) => ({
      "Item name": item.item_title,
      "Quantity": item.quantity,
    })),
  };
}

const triggerClevertapEvent = async (clevertapData: CleverTapData, eventName: string, eventData: any, user?: UserType) => {
  if (typeof (window as any).clevertap === 'undefined') {
    initializeClevertap(clevertapData);
    await sleep(2000)
    if (user) {
      const marketingConsent = user.marketing_consent ?? true;
      (window as any)?.clevertap?.onUserLogin?.push({
        Site: {
          Name: user.name,
          Phone: user.phone,
          Identity: user.phone,
          Source: 'Shop Pass',
          ...(user.email && { Email: user.email }),
          "MSG-email": marketingConsent,
          "MSG-push": marketingConsent,
          "MSG-sms": marketingConsent,
          "MSG-whatsapp": marketingConsent,
        }
      });
    }
    (window as any)?.clevertap?.event?.push(eventName, eventData);
    return
  }
  if (isClevertapLoading) {
    await sleep(2000)
  }
  if (user) {
    (window as any)?.clevertap?.onUserLogin?.push({
      Site: {
        Name: user.name,
        Phone: user.phone,
        Identity: user.phone,
        Source: 'Shop Pass',
        ...(user.email && { Email: user.email }),
      }
    });
  }
  (window as any)?.clevertap?.event?.push(eventName, eventData);
};

export const sendCleverTapEvent = (clevertapData: CleverTapData, eventName: EventName, eventData: any, user: UserType) => {
  if (
    (eventName === analyticsEvents.FLO_COOKIE_LOGIN ||
      eventName === analyticsEvents.FLO_AUTH_PHONE_LOADED)
  ) {
    triggerClevertapEvent(clevertapData, ClevertapEvents.CHECKOUT_STARTED, eventData);
  }
  if (eventName === analyticsEvents.FLO_AUTH_LOGIN_COMPLETED || eventName === analyticsEvents.FLO_RETURN_USER_LOGIN || eventName === analyticsEvents.FLO_GUEST_LOGIN) {
    triggerClevertapEvent(clevertapData, ClevertapEvents.LOGIN_COMPLETED, eventData, user);
  }
  if (eventName === analyticsEvents.FLO_ADDRESS_SELECTED || eventName === analyticsEvents.FLO_ADDRESS_NEW_FORM_SUBMITTED || eventName === analyticsEvents.FLO_ADDRESS_EDIT_FORM_SUBMITTED) {
    triggerClevertapEvent(clevertapData, ClevertapEvents.ADDRESS_SUBMITTED, eventData);
  }
  if (eventName === analyticsEvents.FLO_PAYMENT_METHOD_SELECTED) {
    triggerClevertapEvent(clevertapData, ClevertapEvents.PAYMENT_INITIATED, eventData);
  }
  if (eventName === analyticsEvents.FLO_COUPON_SUCCESS) {
    triggerClevertapEvent(clevertapData, ClevertapEvents.COUPON_SUCCESS, eventData);
  }
  if (eventName === analyticsEvents.FLO_COUPON_FAILED) {
    triggerClevertapEvent(clevertapData, ClevertapEvents.COUPON_FAILED, eventData);
  }
};